import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, MenuItem, Radio, RadioGroup, FormControlLabel, Checkbox } from '@mui/material';
import { AppDispatch, RootState } from '../../../redux/store'; 
import { uploadFirmwareWithProgress } from '../../../redux/actions/firmware.actions';
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import { useSearchParams } from "react-router-dom";
import MDEditor from '@uiw/react-md-editor';
import { palName } from '../../../utils/brand';
import usePermissionCheck from '../../../hooks/usePermissionCheck';
import ModalShell from "../../../components/common/Modals/ModalShell";

const DESCRIPTION_MAX_LENGTH = 2000;

const UploadFirmwareModal: React.FC<ModalProps> = ({open, setOpen}) => {
    const searchParams = useSearchParams()[0];
    const handleClose = () => {
      handleReset();
      setOpen(false);
    };

    const handleReset = () => {
      setSubmitted(false);
      setFile(null);
      setType('yocto');
      setVersion('');
      setDescription('');
      setUploadProgress(0);
      setLatest(false);
      setDev(false);
      setAdmin(false);
    };

    const dispatch = useDispatch<AppDispatch>();
    const { accessToken } = useSelector((state: RootState) => state.user);
    const {loading, error} = useSelector((state: RootState) => state.firmware);
    const { canAccess: canAssignAdmin } = usePermissionCheck(['assign_admin_firmware']);
    const [submitted, setSubmitted] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const [file, setFile] = useState<File | null>(null);
    const [type, setType] = useState<'yocto' | 'lpc' | 'spaboy'>('yocto');
    const [version, setVersion] = useState('');
    const [description, setDescription] = useState('');
    const [is_latest, setLatest] = useState(false);
    const [is_dev, setDev] = useState(false);
    const [is_admin, setAdmin] = useState(false);

    useEffect(() => {
      if (is_dev && is_latest) {
        setLatest(false);
      }
      // Admin firmware must be a dev version.
      if (!is_dev && is_admin) {
        setAdmin(false);
      }
    }, [is_dev]);
  
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        setFile(event.target.files[0]);
      }
    };
  
    const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault();
      if (!file || !accessToken) return;

      try {
        await dispatch(uploadFirmwareWithProgress({
          file,
          type,
          version,
          description,
          is_latest,
          is_dev,
          is_admin,
          onProgress: (progress:any) => setUploadProgress(progress)
        })).unwrap();
      } catch {
        // The rejection message is stored in firmware.error and shown below.
      }

      setSubmitted(true);
    };

    const showForm = !submitted || loading;

    return (
      <ModalShell
        open={open}
        onClose={handleClose}
        size={showForm ? "lg" : "sm"}
        tone={showForm ? undefined : error ? "error" : "success"}
        title={showForm ? "Upload firmware" : error ? "An error occurred" : "Firmware uploaded"}
        actions={showForm ?
          <>
            <button className="btn" onClick={handleClose}>Close</button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading || !accessToken || description==="" || version==="" || !file}
            >
              {loading ? "Uploading…" : "Upload"}
            </button>
          </>
          :
          <>
            <button className="btn btn-ghost dlg-actions-start" onClick={handleReset}>{error ? "Try again" : "Upload another"}</button>
            <button className="btn btn-primary" onClick={handleClose}>Close</button>
          </>
        }
      >
        {!showForm ?
          (error ? <p>{error}</p> : <p>Firmware uploaded successfully.</p>)
          :
          <form onSubmit={handleSubmit} style={{display:'contents'}}>
            <div className="field">
              <label className="field-label" htmlFor="file">File *</label>
              <input
                className="input"
                type="file"
                id="file"
                accept=".bin,.swu,.s19,.pages"
                onChange={handleFileChange}
                required
                disabled={loading}
              />
              <small className="dlg-muted">
                Note: Files will be renamed to "{type}_{version}.extension" when uploaded.
              </small>
            </div>

            <div className="dlg-row">
              <div className="field">
                <label className="field-label" id="type-label">Type *</label>
                <Select
                  labelId="type-label"
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value as 'yocto' | 'lpc' | 'spaboy')}
                  required
                  disabled={loading}
                  size="small"
                >
                  <MenuItem value="yocto">Yocto</MenuItem>
                  <MenuItem value="lpc">LPC</MenuItem>
                  <MenuItem value="spaboy">{palName}</MenuItem>
                </Select>
              </div>
              <div className="field">
                <label className="field-label" htmlFor="version">Version *</label>
                <input
                  className="input"
                  id="version"
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="dlg-section">
              <span className="dlg-label">Release</span>
              <FormControlLabel control={<Checkbox checked={is_latest??false} disabled={is_dev??false} onChange={(e)=>setLatest(e.target.checked)}/>} label="Latest" />
              {is_dev && (
                <small className="dlg-muted">Dev firmware cannot be set as latest.</small>
              )}
            </div>

            <div className="dlg-section">
              <span className="dlg-label" id="firmware-visibility-radio-group">Visibility *</span>
              <RadioGroup
                aria-labelledby="firmware-visibility-radio-group"
                defaultValue="public"
                name="visibility-buttons-group"
                row
                onChange={(e)=>setDev(e.target.value==='dev')}
              >
                <FormControlLabel value="public" control={<Radio />} label="Public" />
                <FormControlLabel value="dev" control={<Radio />} label="Dev Only" />
              </RadioGroup>
              {canAssignAdmin && is_dev && (
                <FormControlLabel
                  control={<Checkbox size="small" checked={is_admin} disabled={loading} onChange={(e)=>setAdmin(e.target.checked)}/>}
                  label="Admin firmware"
                />
              )}
            </div>

            <div className="dlg-section" data-color-mode={localStorage.getItem('data-theme') ?? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')}>
              <span className="dlg-label">Description *</span>
              <MDEditor value={description} onChange={(value) => setDescription(value??"")} aria-required={true} textareaProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}/>
              <small className="dlg-muted" style={{ alignSelf: 'flex-end', ...(description.length >= DESCRIPTION_MAX_LENGTH ? { color: 'var(--bad)' } : {}) }}>
                {description.length}/{DESCRIPTION_MAX_LENGTH}
              </small>
            </div>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="dlg-section">
                <span className="dlg-label">Uploading</span>
                <div className="dlg-progress">
                  <div className="dlg-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(uploadProgress)}>
                    <div className="dlg-progress-bar" style={{width: `${uploadProgress}%`}}/>
                  </div>
                  <span className="dlg-progress-value">{Math.round(uploadProgress)}%</span>
                </div>
              </div>
            )}
          </form>
        }
      </ModalShell>
    );
};

export default UploadFirmwareModal;
